import React from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './auth.jsx'
import { AdminPortalGate, useMe } from './me.jsx'
import { FlagsProvider } from './flags/FlagsProvider.jsx'
import { AdminSidebar } from './components/AdminSidebar.jsx'
import { OrgsList } from './pages/OrgsList.jsx'
import { OrgDetail } from './pages/OrgDetail.jsx'
import { AuditLog } from './pages/AuditLog.jsx'
import { Analytics } from './pages/Analytics.jsx'
import { CostReport } from './pages/CostReport.jsx'
import { CallbackPage } from './pages/CallbackPage.jsx'
import { UserSettingsPage } from './pages/UserSettingsPage.jsx'
import { NotificationsPage } from './pages/NotificationsPage.jsx'
import { FeaturesPage } from './pages/FeaturesPage.jsx'
import { OrgAdminRoute } from './org-admin/OrgAdminRoute.jsx'
import { OverviewPage } from './org-admin/pages/OverviewPage.jsx'
import { UsersPage } from './org-admin/pages/UsersPage.jsx'
import { OrgPage } from './org-admin/pages/OrgPage.jsx'
import { AnalyticsPage as OrgAnalyticsPage } from './org-admin/pages/AnalyticsPage.jsx'
import { SettingsPage as OrgSettingsPage } from './org-admin/pages/SettingsPage.jsx'
import { SsoSettingsPage } from './org-admin/pages/SsoSettingsPage.jsx'
import { FeedbackTab } from '@components/feedback/FeedbackTab.jsx'
import { FeedbackModal } from '@components/feedback/FeedbackModal.jsx'
import { useFeedback } from './hooks/useFeedback.js'
import { AdminDocsLayout } from './docs/AdminDocsLayout.jsx'

function Shell({ children }) {
  const { signOut } = useAuth()
  const me = useMe()
  const nav = useNavigate()
  const location = useLocation()
  const { feedbackOpen, openFeedbackModal, closeFeedbackModal, submitFeedback } = useFeedback(me)
  const handleScopeChange = (next) => {
    nav(next === 'super' ? '/orgs' : '/org/overview')
  }
  return (
    <div style={styles.layout}>
      <AdminSidebar
        currentPath={location.pathname}
        onNavigate={(p) => nav(p)}
        onSignOut={signOut}
        onOpenFeedback={openFeedbackModal}
        onScopeChange={handleScopeChange}
        me={me}
      />
      <main style={styles.main}>
        <div style={styles.inner}>{children}</div>
      </main>
      <FeedbackTab onClick={openFeedbackModal} />
      <FeedbackModal open={feedbackOpen} onClose={closeFeedbackModal} onSubmit={submitFeedback} />
    </div>
  )
}

function RootRedirect() {
  const me = useMe()
  if (me?.isSuperAdmin) return <Navigate to="/orgs" replace />
  if (me?.isAdmin) return <Navigate to="/org/overview" replace />
  return <Navigate to="/settings" replace />
}

const styles = {
  layout: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  },
  main: {
    flex: 1,
    overflowY: 'auto',
    scrollbarGutter: 'stable',
    background: 'var(--bg)',
    padding: '24px 32px',
  },
  inner: {
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
}

function OrgOverview() {
  const me = useMe()
  const nav = useNavigate()
  return <OverviewPage onNavigate={(page) => nav(`/org/${page}`)} me={me} />
}

function OrgSettings() {
  const me = useMe()
  return <OrgSettingsPage me={me} />
}

export default function App() {
  return (
    <AuthProvider>
      <FlagsProvider>
        <Routes>
          <Route path="/callback" element={<CallbackPage />} />
          <Route
            path="/*"
            element={
              <AdminPortalGate>
                <Shell>
                  <Routes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/orgs" element={<OrgsList />} />
                    <Route path="/orgs/new" element={<OrgsList showCreate />} />
                    <Route path="/orgs/:orgId" element={<OrgDetail />} />
                    <Route path="/orgs/:orgId/invite" element={<OrgDetail showInvite />} />
                    <Route path="/orgs/:orgId/revoke" element={<OrgDetail showRevoke />} />
                    <Route path="/settings" element={<UserSettingsPage />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/costs" element={<CostReport />} />
                    <Route path="/audit" element={<AuditLog />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/org/overview" element={<OrgAdminRoute><OrgOverview /></OrgAdminRoute>} />
                    <Route path="/org/users" element={<OrgAdminRoute><UsersPage /></OrgAdminRoute>} />
                    <Route path="/org/org" element={<OrgAdminRoute><OrgPage /></OrgAdminRoute>} />
                    <Route path="/org/analytics" element={<OrgAdminRoute><OrgAnalyticsPage /></OrgAdminRoute>} />
                    <Route path="/org/settings" element={<OrgAdminRoute><OrgSettings /></OrgAdminRoute>} />
                    <Route path="/org/sso" element={<OrgAdminRoute><SsoSettingsPage /></OrgAdminRoute>} />
                    <Route path="/docs/*" element={<AdminDocsLayout />} />
                    <Route path="*" element={<RootRedirect />} />
                  </Routes>
                </Shell>
              </AdminPortalGate>
            }
          />
        </Routes>
      </FlagsProvider>
    </AuthProvider>
  )
}
